// @ts-nocheck -- TypeScript migration in progress: this controller follows the
// existing dependency-injected browser-controller pattern.
import { analyzePilotObservation } from "../analysis/pilot-observation.js";
import { databaseFlightSpecialTestEndpoint } from "../core/config.js";
import { formatElapsedTime } from "../core/format.js";
import { escapeHtml } from "../core/html.js";

const PILOT_OBSERVATION_KIND = "pilot-observation";
const PILOT_OBSERVATION_NOTE_MAX_LENGTH = 500;
const PILOT_OBSERVATION_CACHE_MAX = 24;

const SYMPTOM_LABELS = Object.freeze({
  roughness: "Roughness",
  vibration: "Vibration",
  "power-loss": "Power loss",
  hesitation: "Hesitation",
  other: "Other",
});

const SEVERITY_LABELS = Object.freeze({
  mild: "Mild",
  moderate: "Moderate",
  severe: "Severe",
});

function normalizedSavedRows(rows = []) {
  return (Array.isArray(rows) ? rows : [])
    .filter((row) => row && typeof row === "object")
    .slice()
    .sort((a, b) => Number(a.start_time ?? a.result_json?.window?.startTime ?? 0)
      - Number(b.start_time ?? b.result_json?.window?.startTime ?? 0));
}

function elapsedInputValue(value) {
  const text = String(value ?? "").trim();
  const parts = text.split(":");
  if (parts.length < 2 || parts.length > 3 || parts.some((part) => !/^\d{1,3}$/.test(part))) return null;
  const numbers = parts.map(Number);
  const seconds = numbers.at(-1);
  const minutes = numbers.at(-2);
  const hours = parts.length === 3 ? numbers[0] : 0;
  if (seconds >= 60 || minutes >= 60) return null;
  return hours * 3600 + minutes * 60 + seconds;
}

function observationFromSaved(row) {
  const pilot = row?.pilot_context_json && typeof row.pilot_context_json === "object" ? row.pilot_context_json : {};
  const result = row?.result_json && typeof row.result_json === "object" ? row.result_json : {};
  const observation = result.observation && typeof result.observation === "object" ? result.observation : {};
  const startTime = Number(row?.start_time ?? result?.window?.startTime);
  const endTime = Number(row?.end_time ?? result?.window?.endTime);
  if (!Number.isFinite(startTime) || !Number.isFinite(endTime) || endTime <= startTime) return null;
  return {
    id: String(row?.id ?? ""),
    window: { startTime, endTime },
    symptomType: String(pilot.symptomType ?? observation.symptomType ?? "other").trim().toLowerCase(),
    severity: String(pilot.severity ?? observation.severity ?? "").trim().toLowerCase(),
    source: String(pilot.source ?? observation.source ?? "pilot").trim().toLowerCase(),
    note: String(pilot.note ?? "").trim(),
    result,
    row,
  };
}

function observationWindowLabel(value) {
  if (!value?.window) return "Marked event";
  return `${formatElapsedTime(value.window.startTime)}-${formatElapsedTime(value.window.endTime)}`;
}

function powerChannelLabel(key) {
  return {
    rpm: "RPM",
    map: "MAP",
    fuelFlow: "fuel flow",
    percentPower: "calculated power",
  }[key] ?? "";
}

function joinedLabels(values = []) {
  const labels = values.filter(Boolean);
  if (labels.length < 2) return labels[0] ?? "";
  if (labels.length === 2) return `${labels[0]} and ${labels[1]}`;
  return `${labels.slice(0, -1).join(", ")}, and ${labels.at(-1)}`;
}

function telemetryReviewCalloutCopy(analysis) {
  const assessment = analysis?.symptomAssessment ?? {};
  const symptom = String(analysis?.observation?.symptomType ?? "other");
  const signals = Array.isArray(analysis?.observedSignals) ? analysis.observedSignals : [];
  const isolatedEgt = signals.find((signal) => signal?.key === "isolated-egt") ?? null;
  const powerChange = signals.find((signal) => signal?.key === "coordinated-power-change") ?? null;
  const rpmChange = signals.find((signal) => signal?.key === "rpm-instability") ?? null;
  const standout = analysis?.egt?.standout ?? null;
  const cylinder = Number.isFinite(Number(isolatedEgt?.cylinder ?? standout?.cylinder))
    ? Number(isolatedEgt?.cylinder ?? standout?.cylinder)
    : null;
  const amplitude = Number(standout?.peerRelative?.amplitudeF);
  const cylinderLabel = isolatedEgt?.cylinderLabel ?? standout?.cylinderLabel
    ?? (Number.isFinite(cylinder) ? `Cylinder ${cylinder}` : "One trace");
  const broadWindow = (Array.isArray(analysis?.dataCoverage?.limitations) ? analysis.dataCoverage.limitations : [])
    .some((item) => /searched shorter subwindows/i.test(String(item)));
  const availablePowerChannelLabels = (
    Array.isArray(analysis?.powerContext?.availableChannels) ? analysis.powerContext.availableChannels : []
  ).map(powerChannelLabel).filter(Boolean);
  const availablePowerChannels = joinedLabels(availablePowerChannelLabels);
  const changedPowerChannels = joinedLabels(
    (Array.isArray(powerChange?.channels) ? powerChange.channels : []).map(powerChannelLabel),
  );

  let signalHeadline = "";
  if (isolatedEgt) {
    signalHeadline = isolatedEgt.powerConfounded
      ? `${cylinderLabel} EGT changed while power was moving.`
      : `${cylinderLabel} EGT moved${Number.isFinite(amplitude) ? ` about ${Math.round(amplitude)} F` : ""} versus the others.`;
  } else if (powerChange) {
    signalHeadline = `${changedPowerChannels || "Power channels"} ${powerChange.direction === "decrease" ? "decreased" : powerChange.direction === "increase" ? "increased" : "changed"} together.`;
  } else if (rpmChange) {
    const rpmRange = Number(analysis?.powerContext?.rpm?.range);
    signalHeadline = `RPM varied${Number.isFinite(rpmRange) ? ` by about ${Math.round(rpmRange)} RPM` : ""}.`;
  }
  if (isolatedEgt && powerChange) {
    const direction = powerChange.direction === "decrease"
      ? "decreased"
      : powerChange.direction === "increase"
        ? "increased"
        : "changed";
    signalHeadline = `${signalHeadline.replace(/\.$/, "")}; ${changedPowerChannels || "power channels"} ${direction} together.`;
  } else if (isolatedEgt && analysis?.powerContext?.stable === true && availablePowerChannels) {
    signalHeadline = `${signalHeadline.replace(/\.$/, "")}; ${availablePowerChannels} ${availablePowerChannelLabels.length === 1 ? "was" : "were"} steady.`;
  }
  if (broadWindow && signalHeadline) {
    signalHeadline = `A shorter segment showed ${signalHeadline.charAt(0).toLowerCase()}${signalHeadline.slice(1)}`;
  }
  const engineLabel = analysis?.correlation?.engineLabel;
  if (!isolatedEgt && (analysis?.engines?.length ?? 0) > 1 && engineLabel && signalHeadline) {
    signalHeadline = `${engineLabel}: ${signalHeadline}`;
  }

  switch (assessment.status) {
    case "telemetry-match":
      return {
        headline: signalHeadline || "A telemetry change matched the reported time.",
        detail: symptom === "vibration"
          ? "Telemetry changed in the selected portion; vibration is not measured."
          : "Telemetry changed in the selected portion; cause unknown.",
      };
    case "power-change-observed":
      return {
        headline: signalHeadline || "Power channels decreased together.",
        detail: "Commanded versus uncommanded cannot be determined.",
      };
    case "telemetry-change-observed":
      return {
        headline: signalHeadline || "A telemetry change stood out.",
        detail: "Telemetry only; no symptom or cause assigned.",
      };
    case "context-limited":
      return {
        headline: signalHeadline || "Telemetry changed while power was moving.",
        detail: symptom === "vibration"
          ? "Power changes limit the comparison; vibration is not measured."
          : "Power changes prevent a clean comparison.",
      };
    case "no-recorded-power-loss":
      return {
        headline: availablePowerChannelLabels.length >= 2
          ? `No coordinated drop across ${availablePowerChannels}.`
          : "No coordinated power reduction stood out.",
        detail: "A very brief or unlogged change is still possible.",
      };
    case "no-clear-telemetry-change":
      return {
        headline: "No clear telemetry change stood out.",
        detail: "What was felt may not appear in this log.",
      };
    case "no-specific-match":
      return {
        headline: symptom === "other"
          ? "No clear telemetry change stood out."
          : "No clear EGT or RPM match stood out.",
        detail: symptom === "vibration"
          ? "This does not dismiss the report; vibration is not measured."
          : "This does not dismiss the pilot report.",
      };
    default:
      return {
        headline: "Marked event ready for review.",
        detail: "",
      };
  }
}

export function createPilotObservationController({
  aircraftProfileForEntry,
  appReadOnlyMode,
  readOnlyActionMessage,
  renderAiSummaryPanel,
  resetGeneratedReport,
  revokeCachedFlightReport,
  scheduleChartDraw,
  selectedDatabaseFlightId,
  syncReadOnlyUi,
  getFlightData,
  getSelectedFlight,
  getFlightManifest,
  getCurrentParserMetadata,
  getLastRequestedFlightId,
  getPilotObservationState,
  getFlightReportMetadataChecked,
  getFlightReportMetadataPending,
  getFlightReportStoredMetadata,
  flightReportMetadataKey,
  chartGestureHintBaseText = () => "Drag to zoom • Right-drag to pan",
  onOpenPilotObservation = () => {},
} = {}) {
  const state = getPilotObservationState();
  const cache = new Map();
  let savedRows = [];
  let saveState = { status: "idle", message: "" };
  let selectedSavedId = "";

  function remember(flightId, rows) {
    if (!flightId) return;
    cache.set(flightId, normalizedSavedRows(rows));
    if (cache.size > PILOT_OBSERVATION_CACHE_MAX) cache.delete(cache.keys().next().value);
  }

  function clearPilotObservationPersistenceState() {
    savedRows = [];
    saveState = { status: "idle", message: "" };
    selectedSavedId = "";
  }

  function resetPilotObservationSelectionState({ close = true } = {}) {
    state.open = close ? false : state.open;
    state.selecting = false;
    state.window = null;
    state.symptomType = "roughness";
    state.severity = "";
    state.source = "pilot";
    state.note = "";
    selectedSavedId = "";
  }

  function dismissPilotObservationPanel({ clear = true } = {}) {
    state.open = false;
    state.selecting = false;
    if (clear) state.window = null;
    renderPilotObservationPanel({ syncInputs: true });
    scheduleChartDraw("explorerChart");
  }

  function pilotObservationDetectionWindows() {
    if (!state.window) return [];
    return [{
      key: "pilot-observation",
      sourceKey: "pilot-observation",
      label: `Pilot: ${SYMPTOM_LABELS[state.symptomType] ?? "Observation"}`,
      evidenceLabel: `Pilot: ${SYMPTOM_LABELS[state.symptomType] ?? "Observation"}`,
      startTime: state.window.startTime,
      endTime: state.window.endTime,
    }];
  }

  function syncEvidencePacketMetadata(payload = {}) {
    const packet = payload.evidencePacket;
    if (!getSelectedFlight()?.database || !packet?.id) return;
    const updates = {
      currentEvidencePacketId: packet.id,
      ...(packet.evidence_sha256 ? { currentEvidenceSha256: packet.evidence_sha256 } : {}),
    };
    Object.assign(getSelectedFlight().database, updates);
    const manifestEntry = getFlightManifest().find((entry) => entry.id === getSelectedFlight().id);
    if (manifestEntry?.database) Object.assign(manifestEntry.database, updates);
  }

  function invalidateReport(entry = getSelectedFlight()) {
    if (!entry?.id) return;
    revokeCachedFlightReport(entry.id);
    getFlightReportStoredMetadata().delete(entry.id);
    const metadataKey = flightReportMetadataKey(entry);
    if (metadataKey) {
      getFlightReportMetadataChecked().delete(metadataKey);
      getFlightReportMetadataPending().delete(metadataKey);
    }
    const updates = {
      reportId: null,
      reportFileName: null,
      reportGeneratedAt: null,
      reportByteSize: null,
    };
    if (entry.database) Object.assign(entry.database, updates);
    const manifestEntry = getFlightManifest().find((item) => item.id === entry.id);
    if (manifestEntry?.database) Object.assign(manifestEntry.database, updates);
  }

  function applyMutation(payload = {}, message = "") {
    if (Array.isArray(payload.specialTestResults)) {
      savedRows = normalizedSavedRows(payload.specialTestResults);
      remember(selectedDatabaseFlightId(getSelectedFlight()), savedRows);
    }
    if (selectedSavedId && !savedRows.some((row) => String(row.id) === selectedSavedId)) selectedSavedId = "";
    syncEvidencePacketMetadata(payload);
    invalidateReport();
    resetGeneratedReport();
    renderAiSummaryPanel();
    saveState = {
      status: "saved",
      message: message || `${savedRows.length} saved observation${savedRows.length === 1 ? "" : "s"}`,
    };
  }

  async function loadSavedPilotObservations(entry = getSelectedFlight()) {
    clearPilotObservationPersistenceState();
    const flightId = selectedDatabaseFlightId(entry);
    if (!flightId) return;
    if (cache.has(flightId)) {
      savedRows = normalizedSavedRows(cache.get(flightId));
      renderPilotObservationPanel({ syncInputs: false });
      return;
    }
    try {
      const response = await fetch(databaseFlightSpecialTestEndpoint(flightId, PILOT_OBSERVATION_KIND), { cache: "no-store" });
      if (getLastRequestedFlightId() !== entry.id) return;
      if (response.status === 404) {
        remember(flightId, []);
        return;
      }
      const payload = await response.json().catch(() => ({}));
      if (!response.ok) throw new Error(payload?.error ?? `Pilot observation API HTTP ${response.status}`);
      savedRows = normalizedSavedRows(payload.specialTestResults ?? [payload.specialTestResult].filter(Boolean));
      remember(flightId, savedRows);
    } catch (error) {
      console.warn("Could not load pilot observations", error);
      saveState = { status: "error", message: "Saved observations unavailable" };
    }
    renderPilotObservationPanel({ syncInputs: false });
  }

  function currentAnalysis() {
    if (!state.window) return null;
    return analyzePilotObservation(getFlightData(), {
      window: state.window,
      symptomType: state.symptomType,
      severity: state.severity,
      source: state.source,
      aircraftProfile: aircraftProfileForEntry(getSelectedFlight()),
      parserMetadata: getCurrentParserMetadata(),
    });
  }

  function setWindow(startTime, endTime) {
    const start = Number(startTime);
    const end = Number(endTime);
    if (!Number.isFinite(start) || !Number.isFinite(end) || Math.abs(end - start) < 1) return;
    state.open = true;
    state.selecting = false;
    state.window = { startTime: Math.min(start, end), endTime: Math.max(start, end) };
    selectedSavedId = "";
    saveState = { status: "dirty", message: "Review and save this observation" };
    renderPilotObservationPanel({ syncInputs: true });
    scheduleChartDraw("explorerChart");
  }

  function commitExplorerPilotObservationSelection({ startPoint, endPoint } = {}) {
    if (!Number.isFinite(startPoint?.elapsedSeconds) || !Number.isFinite(endPoint?.elapsedSeconds)) return;
    setWindow(startPoint.elapsedSeconds, endPoint.elapsedSeconds);
  }

  function selectSavedObservation(resultId) {
    const row = savedRows.find((candidate) => String(candidate?.id ?? "") === String(resultId ?? ""));
    const saved = observationFromSaved(row);
    if (!saved) return;
    onOpenPilotObservation();
    state.open = true;
    state.selecting = false;
    state.window = saved.window;
    state.symptomType = saved.symptomType;
    state.severity = saved.severity;
    state.source = saved.source;
    state.note = saved.note;
    selectedSavedId = saved.id;
    saveState = { status: "saved", message: "Loaded saved observation" };
    renderPilotObservationPanel({ syncInputs: true });
    scheduleChartDraw("explorerChart");
  }

  async function savePilotObservation() {
    if (appReadOnlyMode()) {
      window.alert(readOnlyActionMessage("Saving pilot observations"));
      syncReadOnlyUi();
      return;
    }
    const flightId = selectedDatabaseFlightId();
    if (!flightId || !state.window || !SYMPTOM_LABELS[state.symptomType]) return;
    let analysis = null;
    try {
      analysis = currentAnalysis();
    } catch (error) {
      saveState = { status: "error", message: error.message || "Analysis unavailable" };
      renderPilotObservationPanel({ syncInputs: false });
      return;
    }
    const selectedFlightId = getSelectedFlight()?.id ?? "";
    saveState = { status: "saving", message: "Saving..." };
    renderPilotObservationPanel({ syncInputs: false });
    try {
      const response = await fetch(databaseFlightSpecialTestEndpoint(flightId, PILOT_OBSERVATION_KIND), {
        method: "PUT",
        headers: { "content-type": "application/json" },
        body: JSON.stringify({
          testKind: PILOT_OBSERVATION_KIND,
          testName: "Pilot observation",
          window: state.window,
          pilotContext: {
            symptomType: state.symptomType,
            severity: state.severity || null,
            source: state.source,
            note: state.note.trim(),
          },
          result: analysis,
        }),
      });
      const payload = await response.json().catch(() => ({}));
      if (!response.ok) throw new Error(payload?.error ?? `Pilot observation API HTTP ${response.status}`);
      if (getSelectedFlight()?.id !== selectedFlightId) return;
      selectedSavedId = String(payload?.specialTestResult?.id ?? "");
      applyMutation(payload, payload?.artifactRefreshSkipped
        ? "Saved. Re-analyze flight to update AI and reports"
        : "Saved for the AI summary and report");
    } catch (error) {
      console.error("Pilot observation save failed", error);
      saveState = { status: "error", message: error.message || "Save failed" };
    } finally {
      renderPilotObservationPanel({ syncInputs: false });
      scheduleChartDraw("explorerChart");
    }
  }

  async function deletePilotObservation(resultId) {
    if (!resultId) return;
    if (appReadOnlyMode()) {
      window.alert(readOnlyActionMessage("Deleting pilot observations"));
      syncReadOnlyUi();
      return;
    }
    const flightId = selectedDatabaseFlightId();
    if (!flightId) return;
    saveState = { status: "deleting", message: "Deleting..." };
    renderPilotObservationPanel({ syncInputs: false });
    try {
      const response = await fetch(databaseFlightSpecialTestEndpoint(flightId, PILOT_OBSERVATION_KIND, resultId), { method: "DELETE" });
      const payload = await response.json().catch(() => ({}));
      if (!response.ok) throw new Error(payload?.error ?? `Pilot observation API HTTP ${response.status}`);
      if (String(resultId) === selectedSavedId) {
        selectedSavedId = "";
        state.window = null;
      }
      applyMutation(payload, "Deleted observation");
    } catch (error) {
      console.error("Pilot observation delete failed", error);
      saveState = { status: "error", message: error.message || "Delete failed" };
    } finally {
      renderPilotObservationPanel({ syncInputs: true });
      scheduleChartDraw("explorerChart");
    }
  }

  function savedListHtml() {
    if (!savedRows.length) return "";
    const items = savedRows.map((row) => {
      const saved = observationFromSaved(row);
      if (!saved) return "";
      const label = SYMPTOM_LABELS[saved.symptomType] ?? "Observation";
      const selected = saved.id === selectedSavedId;
      return `
        <div class="pilot-observation-saved-item ${selected ? "is-selected" : ""}">
          <button type="button" data-pilot-observation-load-id="${escapeHtml(saved.id)}">
            <strong>${escapeHtml(label)} · ${escapeHtml(observationWindowLabel(saved))}</strong>
            <span>${escapeHtml(saved.result?.symptomAssessment?.summary ?? saved.result?.correlation?.summary ?? "Saved pilot context")}</span>
          </button>
          ${appReadOnlyMode() ? "" : `<button class="pilot-observation-saved-delete" type="button" data-pilot-observation-delete-id="${escapeHtml(saved.id)}" aria-label="Delete saved ${escapeHtml(label.toLowerCase())} observation">&times;</button>`}
        </div>
      `;
    }).join("");
    return `<div class="pilot-observation-saved-heading"><span>Saved observations</span><strong>${savedRows.length}</strong></div>${items}`;
  }

  function analysisHtml() {
    if (!state.window) return '<p class="pilot-observation-analysis-empty">Drag across the graph to mark when the symptom occurred.</p>';
    let analysis = null;
    try {
      analysis = currentAnalysis();
    } catch (error) {
      return `<p class="pilot-observation-analysis-empty">${escapeHtml(error.message)}</p>`;
    }
    const entries = analysis.engines?.length > 1 ? analysis.engines.map((engine) => ({
      ...analysis,
      ...engine,
      powerContext: engine.power,
      correlation: { status: engine.correlationStatus, engineId: engine.engineId, engineLabel: engine.engineLabel },
    })) : [analysis];
    return entries.map((entry) => {
      const status = entry.symptomAssessment?.status ?? entry.correlation?.status ?? "limited-data";
      const copy = telemetryReviewCalloutCopy(entry);
      return `
      <div class="pilot-observation-analysis-summary" data-correlation-status="${escapeHtml(status)}">
        ${entries.length > 1 ? `<span>${escapeHtml(entry.engineLabel)}</span>` : ""}
        <strong>${escapeHtml(copy.headline)}</strong>
        ${copy.detail ? `<span>${escapeHtml(copy.detail)}</span>` : ""}
      </div>
    `;
    }).join("");
  }

  function syncInputValues() {
    const startInput = document.querySelector("#pilotObservationStartInput");
    const endInput = document.querySelector("#pilotObservationEndInput");
    const note = document.querySelector("#pilotObservationNote");
    if (startInput && document.activeElement !== startInput) startInput.value = state.window ? formatElapsedTime(state.window.startTime) : "";
    if (endInput && document.activeElement !== endInput) endInput.value = state.window ? formatElapsedTime(state.window.endTime) : "";
    if (!state.window) {
      startInput?.classList.remove("is-invalid");
      endInput?.classList.remove("is-invalid");
    }
    if (note && document.activeElement !== note) note.value = state.note;
  }

  function renderPilotObservationPanel({ syncInputs = true } = {}) {
    const toggle = document.querySelector("#explorerPilotObservationToggle");
    const panel = document.querySelector("#pilotObservationPanel");
    const canvas = document.querySelector("#explorerChart");
    const hint = document.querySelector(".explorer-chart-wrap .chart-gesture-hint");
    if (!toggle || !panel) return;
    toggle.classList.toggle("is-active", state.open);
    toggle.setAttribute("aria-expanded", state.open ? "true" : "false");
    panel.hidden = !state.open;
    panel.dataset.status = saveState.status;
    document.querySelectorAll("[data-pilot-observation-symptom]").forEach((button) => {
      const selected = button.dataset.pilotObservationSymptom === state.symptomType;
      button.classList.toggle("is-selected", selected);
      button.setAttribute("aria-pressed", selected ? "true" : "false");
    });
    document.querySelectorAll("[data-pilot-observation-severity]").forEach((button) => {
      const selected = button.dataset.pilotObservationSeverity === state.severity;
      button.classList.toggle("is-selected", selected);
      button.setAttribute("aria-pressed", selected ? "true" : "false");
    });
    const saveButton = document.querySelector("#pilotObservationSave");
    if (saveButton) saveButton.disabled = !state.window
      || !selectedDatabaseFlightId()
      || appReadOnlyMode()
      || ["saving", "deleting"].includes(saveState.status);
    const reselectButton = document.querySelector("#pilotObservationReselect");
    if (reselectButton) {
      reselectButton.hidden = state.selecting;
      reselectButton.classList.remove("is-active");
      reselectButton.setAttribute("aria-pressed", "false");
      reselectButton.textContent = state.window ? "Change selection" : "Select on chart";
    }
    const selectingPrompt = document.querySelector("#pilotObservationSelectingPrompt");
    if (selectingPrompt) {
      selectingPrompt.hidden = !state.selecting;
    }
    const selectionGroup = document.querySelector("#pilotObservationSelectionGroup");
    if (selectionGroup) {
      selectionGroup.dataset.selectionState = state.selecting
        ? "selecting"
        : state.window
          ? "selected"
          : "empty";
    }
    const clearButton = document.querySelector("#pilotObservationClear");
    if (clearButton) {
      clearButton.disabled = !state.window;
    }
    const status = document.querySelector("#pilotObservationSaveStatus");
    if (status) status.textContent = saveState.message;
    const counter = document.querySelector("#pilotObservationNoteCount");
    if (counter) counter.textContent = `${state.note.length}/${PILOT_OBSERVATION_NOTE_MAX_LENGTH}`;
    const analysis = document.querySelector("#pilotObservationAnalysis");
    if (analysis) analysis.innerHTML = analysisHtml();
    const saved = document.querySelector("#pilotObservationSavedList");
    if (saved) saved.innerHTML = savedListHtml();
    if (syncInputs) syncInputValues();
    if (hint) hint.textContent = state.selecting ? "Drag to mark the pilot-reported event" : chartGestureHintBaseText();
    canvas?.classList.toggle("is-marking-pilot-observation", state.open && state.selecting);
  }

  function applyTimeInputs() {
    const startInput = document.querySelector("#pilotObservationStartInput");
    const endInput = document.querySelector("#pilotObservationEndInput");
    if (!startInput || !endInput) return;
    const start = elapsedInputValue(startInput.value);
    const end = elapsedInputValue(endInput.value);
    const maxElapsed = Math.max(...getFlightData().map((point) => Number(point?.elapsedSeconds)).filter(Number.isFinite), 0);
    const startValid = Number.isFinite(start) && start >= 0 && start <= maxElapsed;
    const endValid = Number.isFinite(end) && end > start && end <= maxElapsed;
    startInput.classList.toggle("is-invalid", !startValid);
    endInput.classList.toggle("is-invalid", !endValid);
    if (startValid && endValid) setWindow(start, end);
  }

  function setupPilotObservations() {
    const toggle = document.querySelector("#explorerPilotObservationToggle");
    const panel = document.querySelector("#pilotObservationPanel");
    if (!toggle || !panel) return;
    toggle.addEventListener("click", () => {
      if (state.open) {
        dismissPilotObservationPanel();
        return;
      }
      if (appReadOnlyMode() && !savedRows.length) {
        window.alert(readOnlyActionMessage("Adding pilot observations"));
        return;
      }
      onOpenPilotObservation();
      state.open = true;
      state.selecting = !appReadOnlyMode();
      if (!state.window) selectedSavedId = "";
      renderPilotObservationPanel({ syncInputs: true });
      scheduleChartDraw("explorerChart");
    });
    document.querySelector("#pilotObservationClose")?.addEventListener("click", () => dismissPilotObservationPanel());
    document.querySelector("#pilotObservationCancel")?.addEventListener("click", () => dismissPilotObservationPanel());
    document.querySelector("#pilotObservationSave")?.addEventListener("click", savePilotObservation);
    document.querySelector("#pilotObservationReselect")?.addEventListener("click", () => {
      state.window = null;
      state.selecting = true;
      selectedSavedId = "";
      saveState = { status: "dirty", message: "" };
      renderPilotObservationPanel({ syncInputs: true });
      scheduleChartDraw("explorerChart");
    });
    document.querySelector("#pilotObservationClear")?.addEventListener("click", () => {
      if (!state.window) return;
      state.window = null;
      state.selecting = !appReadOnlyMode();
      selectedSavedId = "";
      saveState = { status: "dirty", message: "" };
      renderPilotObservationPanel({ syncInputs: true });
      scheduleChartDraw("explorerChart");
    });
    document.querySelectorAll("[data-pilot-observation-symptom]").forEach((button) => {
      button.addEventListener("click", () => {
        state.symptomType = button.dataset.pilotObservationSymptom;
        selectedSavedId = "";
        saveState = { status: "dirty", message: state.window ? "Unsaved changes" : "" };
        renderPilotObservationPanel({ syncInputs: false });
        scheduleChartDraw("explorerChart");
      });
    });
    document.querySelectorAll("[data-pilot-observation-severity]").forEach((button) => {
      button.addEventListener("click", () => {
        const value = button.dataset.pilotObservationSeverity;
        state.severity = state.severity === value ? "" : value;
        selectedSavedId = "";
        saveState = { status: "dirty", message: state.window ? "Unsaved changes" : "" };
        renderPilotObservationPanel({ syncInputs: false });
      });
    });
    ["#pilotObservationStartInput", "#pilotObservationEndInput"].forEach((selector) => {
      const input = document.querySelector(selector);
      input?.addEventListener("change", applyTimeInputs);
      input?.addEventListener("blur", applyTimeInputs);
    });
    document.querySelector("#pilotObservationNote")?.addEventListener("input", (event) => {
      state.note = String(event.target.value ?? "").slice(0, PILOT_OBSERVATION_NOTE_MAX_LENGTH);
      selectedSavedId = "";
      saveState = { status: "dirty", message: state.window ? "Unsaved changes" : "" };
      renderPilotObservationPanel({ syncInputs: false });
    });
    document.querySelector("#pilotObservationSavedList")?.addEventListener("click", (event) => {
      const load = event.target?.closest?.("[data-pilot-observation-load-id]");
      if (load) {
        selectSavedObservation(load.dataset.pilotObservationLoadId);
        return;
      }
      const remove = event.target?.closest?.("[data-pilot-observation-delete-id]");
      if (remove) deletePilotObservation(remove.dataset.pilotObservationDeleteId);
    });
    renderPilotObservationPanel({ syncInputs: true });
  }

  return {
    clearPilotObservationPersistenceState,
    commitExplorerPilotObservationSelection,
    dismissPilotObservationPanel,
    loadSavedPilotObservations,
    pilotObservationDetectionWindows,
    renderPilotObservationPanel,
    resetPilotObservationSelectionState,
    setupPilotObservations,
  };
}
